import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { request } from "./FunctionalApp";

async function api(path: string) {
  const r = await request(path);
  return r.ok ? r.json() : {};
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function startOfWeek(date: Date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - (day === 0 ? 6 : day - 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function getDaysInMonth(year: number, month: number) {
  const days = [];
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  // Pad start
  const startDay = first.getDay() === 0 ? 6 : first.getDay() - 1;
  for (let i = 0; i < startDay; i++) days.push(null);
  for (let d = 1; d <= last.getDate(); d++) days.push(new Date(year, month, d));
  return days;
}

export function CmmsCalendar() {
  const today = new Date();
  const [view, setView] = useState<"month" | "week">("month");
  const [current, setCurrent] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [weekStart, setWeekStart] = useState(startOfWeek(today));
  const [data, setData] = useState<any>({ workOrders: [], ppmPlans: [], shifts: [] });
  const [toggles, setToggles] = useState({ workOrders: true, ppm: true, shifts: true, autonomous: true });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api("/api/maintenance/work-orders/calendar"),
      api("/api/ppm/calendar"),
      api("/api/admin/shifts"),
    ]).then(([wo, ppm, sh]) => {
      setData({
        workOrders: wo.workOrders || [],
        ppmPlans: ppm.plans || [],
        shifts: sh.shifts || [],
      });
      setLoading(false);
    });
  }, []);

  function toggle(key: keyof typeof toggles) {
    setToggles(t => ({ ...t, [key]: !t[key] }));
  }

  function eventsForDay(date: Date) {
    const events: any[] = [];
    const dayName = DAYS[date.getDay() === 0 ? 6 : date.getDay() - 1];

    if (toggles.workOrders) {
      data.workOrders.forEach((wo: any) => {
        const created = new Date(wo.created_at);
        const due = wo.permanent_repair_due_at ? new Date(wo.permanent_repair_due_at) : null;
        if (isSameDay(created, date) && wo.status !== "COMPLETED" && wo.status !== "CANCELLED") {
          events.push({ type: "wo-open", label: `WO: ${wo.title || wo.number}`, priority: wo.priority, id: wo.id });
        }
        if (due && isSameDay(due, date)) {
          events.push({ type: "wo-due", label: `Due: ${wo.title || wo.number}`, priority: wo.priority, id: wo.id });
        }
      });
    }

    if (toggles.ppm) {
      data.ppmPlans.forEach((p: any) => {
        if (!p.next_due_at) return;
        const due = new Date(p.next_due_at);
        if (isSameDay(due, date)) {
          const isAuto = !!p.autonomous_group_id;
          if (isAuto && !toggles.autonomous) return;
          if (!isAuto || toggles.ppm) {
            events.push({ type: isAuto ? "autonomous" : "ppm", label: p.asset_name ? `${p.name} (${p.asset_name})` : p.name });
          }
        }
      });
    }

    if (toggles.shifts) {
      data.shifts.forEach((s: any) => {
        const shiftDays: string[] = Array.isArray(s.days) ? s.days : JSON.parse(s.days || "[]");
        if (shiftDays.includes(dayName)) {
          events.push({ type: "shift", label: `${s.name}: ${s.start_time}–${s.end_time}` });
        }
      });
    }

    return events;
  }

  const days = view === "month"
    ? getDaysInMonth(current.getFullYear(), current.getMonth())
    : Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  function prev() {
    if (view === "month") setCurrent(new Date(current.getFullYear(), current.getMonth() - 1, 1));
    else setWeekStart(addDays(weekStart, -7));
  }
  function next() {
    if (view === "month") setCurrent(new Date(current.getFullYear(), current.getMonth() + 1, 1));
    else setWeekStart(addDays(weekStart, 7));
  }
  function goToday() {
    setCurrent(new Date(today.getFullYear(), today.getMonth(), 1));
    setWeekStart(startOfWeek(today));
  }

  const title = view === "month"
    ? `${MONTHS[current.getMonth()]} ${current.getFullYear()}`
    : `${addDays(weekStart, 0).toLocaleDateString("en-GB", { day:"numeric", month:"short" })} – ${addDays(weekStart, 6).toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" })}`;

  return (
    <>
      <div className="func-heading">
        <div><h1>Maintenance calendar</h1></div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <div className="cal-toggles">
            <button className={`cal-toggle wo ${toggles.workOrders ? "on" : ""}`} onClick={() => toggle("workOrders")}>Work orders</button>
            <button className={`cal-toggle ppm ${toggles.ppm ? "on" : ""}`} onClick={() => toggle("ppm")}>PPM</button>
            <button className={`cal-toggle autonomous ${toggles.autonomous ? "on" : ""}`} onClick={() => toggle("autonomous")}>Autonomous</button>
            <button className={`cal-toggle shift ${toggles.shifts ? "on" : ""}`} onClick={() => toggle("shifts")}>Shifts</button>
          </div>
          <div className="cal-view-toggle">
            <button className={view === "month" ? "selected" : ""} onClick={() => setView("month")}>Month</button>
            <button className={view === "week" ? "selected" : ""} onClick={() => setView("week")}>Week</button>
          </div>
        </div>
      </div>

      <section className="func-panel cal-panel">
        <div className="cal-nav">
          <button onClick={prev}><ChevronLeft size={16} /></button>
          <h2>{title}</h2>
          <button onClick={next}><ChevronRight size={16} /></button>
          <button className="secondary" onClick={goToday} style={{marginLeft:8}}>Today</button>
        </div>

        <div className="cal-grid" style={{"--cols": 7} as any}>
          {DAYS.map(d => <div key={d} className="cal-day-header">{d}</div>)}
          {days.map((date, i) => {
            if (!date) return <div key={`empty-${i}`} className="cal-cell cal-empty" />;
            const events = eventsForDay(date);
            const isToday = isSameDay(date, today);
            const isCurrentMonth = view === "week" || date.getMonth() === current.getMonth();
            return (
              <div key={date.toISOString()} className={`cal-cell ${isToday ? "cal-today" : ""} ${!isCurrentMonth ? "cal-other-month" : ""}`}>
                <span className="cal-date">{date.getDate()}</span>
                <div className="cal-events">
                  {loading ? null : events.slice(0, 4).map((e, j) => (
                    <div key={j} className={`cal-event cal-event-${e.type}`} title={e.label}>
                      {e.label}
                    </div>
                  ))}
                  {events.length > 4 && <div className="cal-event-more">+{events.length - 4} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}
